import { useCallback, useEffect, useState } from "react";
import { getIntegrationFailures } from "@/network/network";
import { useSwivuBryntumContext } from "@/react/hooks/useSwivuBryntumContext";
import { useBoardPermissions } from "@/react/components/entityViews/settings/hooks/useBoardPermissions";
import { getApiErrorMessage } from "./errors";
import type {
  IntegrationFailureEvent,
  IntegrationFailureListParams,
  IntegrationProvider,
} from "./types";

const DEFAULT_LIMIT = 20;

export function useIntegrationFailures(initialProvider?: IntegrationProvider) {
  const { selectedBoard } = useSwivuBryntumContext();
  const { isBoardOwner } = useBoardPermissions();

  const boardId = selectedBoard?.id ? Number(selectedBoard.id) : undefined;

  const [provider, setProvider] = useState<IntegrationProvider | undefined>(initialProvider);
  const [startDate, setStartDate] = useState<string | undefined>();
  const [endDate, setEndDate] = useState<string | undefined>();
  const [results, setResults] = useState<IntegrationFailureEvent[]>([]);
  const [count, setCount] = useState(0);
  const [offset, setOffset] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchFailures = useCallback(
    async (nextOffset = 0, append = false) => {
      if (!boardId || !isBoardOwner) return;

      const params: IntegrationFailureListParams = {
        limit: DEFAULT_LIMIT,
        offset: nextOffset,
      };
      if (provider) params.provider = provider;
      if (startDate && endDate) {
        params.start_date = startDate;
        params.end_date = endDate;
      }

      setIsLoading(true);
      setError(null);

      try {
        const response = await getIntegrationFailures(boardId, params);
        const data = response.data;
        setCount(data.count ?? 0);
        setResults((prev) => (append ? [...prev, ...(data.results ?? [])] : data.results ?? []));
        setOffset(nextOffset);
      } catch (err) {
        setError(getApiErrorMessage(err, "Failed to load integration failures."));
        if (!append) {
          setResults([]);
          setCount(0);
        }
      } finally {
        setIsLoading(false);
      }
    },
    [boardId, isBoardOwner, provider, startDate, endDate]
  );

  useEffect(() => {
    if (!isBoardOwner || !boardId) {
      setResults([]);
      setCount(0);
      return;
    }
    void fetchFailures(0, false);
  }, [boardId, isBoardOwner, provider, startDate, endDate, fetchFailures]);

  const loadMore = useCallback(() => {
    const nextOffset = offset + DEFAULT_LIMIT;
    if (nextOffset >= count || isLoading) return;
    void fetchFailures(nextOffset, true);
  }, [offset, count, isLoading, fetchFailures]);

  const refresh = useCallback(() => {
    void fetchFailures(0, false);
  }, [fetchFailures]);

  const hasMore = offset + DEFAULT_LIMIT < count;

  return {
    isBoardOwner,
    results,
    count,
    isLoading,
    error,
    provider,
    setProvider,
    startDate,
    setStartDate,
    endDate,
    setEndDate,
    loadMore,
    refresh,
    hasMore,
  };
}
